import { useEffect, useState } from "react";
import useQueryParams from "utils/useQueryParams";
import { displayMember, useAnalytics } from "JS/analyticsAPI";
import { MemberActivity } from "./MemberActivity";
import "Styles/pages/analytics/lastVisitMembers.css";
export function LastVisitMembers({
  options,
  params,
  days,
  channel,
  onExpand,
  status,
  onClearStatus,
  onClearChannel,
}) {
  const { searchParams, updateQueryParams } = useQueryParams();
  const pageNumber = Number(searchParams.get("page") || 1);
  const page =
    Number.isInteger(pageNumber) && pageNumber >= 1 && pageNumber <= 100000
      ? pageNumber
      : 1;
  const search = (searchParams.get("search") || "").slice(0, 100);
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [composition, setComposition] = useState(null);
  // 완성형 음절도 IME 세션이 유지될 수 있어 조합 문자열을 따로 확인한다.
  const compositionBlocked = composition !== null && !/^[가-힣]+$/.test(composition);
  const searchPending = compositionBlocked || search !== debouncedSearch;
  useEffect(() => {
    if (compositionBlocked) return;
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search, compositionBlocked]);
  const setPage = (page) => updateQueryParams({ page, member: null });
  const selectPage = (lastPage) =>
    updateQueryParams({ lastPage, page: null, member: null });
  const query = {
    ...params,
    observation_status: status === "pending" ? "pending" : "complete",
    behavior_state:
      options.behavior_states.find((s) => s.label === status)?.id || "all",
  };
  const groupsRequest = useAnalytics("last-pages", query);
  const groups = groupsRequest.data?.groups || [];
  const selected = groups.some(
    (g) => g.page_key === searchParams.get("lastPage"),
  )
    ? searchParams.get("lastPage")
    : "all";
  const membersRequest = useAnalytics(
    "members",
    {
      ...query,
      last_page_key: selected,
      search: debouncedSearch,
      page,
      limit: 6,
    },
    Boolean(groupsRequest.data) && !searchPending,
  );
  const active = groups.find((g) => g.page_key === selected);
  const list = (membersRequest.data?.items || []).map((m) =>
    displayMember(m, options),
  );
  return (
    <>
      <div className="lv-context">
        <span>
          <b>{channel === "all" ? "전체 유입 경로" : channel}</b> · 가입 후{" "}
          {days}일 · {status === "pending" ? "관찰 중" : "관찰 완료"}{" "}
          {groupsRequest.data?.total_members ?? "—"}명
        </span>
        <div className="lv-filters">
          {channel !== "all" && (
            <button
              onClick={onClearChannel}
              aria-label={`${channel} 유입 경로 선택 해제`}
            >
              {channel} ×
            </button>
          )}
          {status !== "all" && (
            <button onClick={onClearStatus}>
              {status === "pending" ? "관찰 중" : status} ×
            </button>
          )}
          {active && (
            <button
              onClick={() => {
                selectPage(null);
              }}
            >
              {active.label} ×
            </button>
          )}
        </div>
      </div>
      <div className="lv-workspace">
        <section className="lv-chart">
          <h3>마지막 방문 페이지</h3>
          <p>회원 수 · 막대를 선택해 회원 확인</p>
          {groupsRequest.loading && <p role="status">페이지 집계 조회 중…</p>}
          {groupsRequest.error && (
            <p role="alert">
              {groupsRequest.error}
              <button onClick={groupsRequest.retry}>다시 시도</button>
            </p>
          )}
          <div className="lv-bars">
            {groups.map((g) => (
              <button
                key={g.page_key}
                aria-pressed={selected === g.page_key}
                onClick={() => {
                  selectPage(selected === g.page_key ? null : g.page_key);
                }}
              >
                <span className="lv-bar-label">
                  <span>{g.label}</span>
                  <b>
                    {g.members}명 <small>{Math.round(g.share)}%</small>
                  </b>
                </span>
                <span className="lv-bar-track">
                  <span
                    style={{
                      width: `${g.share}%`,
                    }}
                  />
                </span>
              </button>
            ))}
          </div>
          {!groupsRequest.loading && !groupsRequest.error && !groups.length && (
            <p>조건에 맞는 기록이 없습니다.</p>
          )}
          <p className="lv-explanation">
            회원당 마지막 페이지 한 곳에 집계합니다. 마지막 방문이 영구 이탈을
            뜻하지는 않습니다.
          </p>
        </section>
        <div className="lv-detail">
          <div className="lv-list-head">
            <h3>
              {active?.label || "전체"} · 회원{" "}
              {membersRequest.data?.pagination.total ?? "—"}명
            </h3>
            <input
              aria-label="회원 검색"
              placeholder="회원 이름 또는 ID 검색"
              maxLength={100}
              value={search}
              onCompositionStart={(e) => setComposition(e.data || "")}
              onCompositionUpdate={(e) => setComposition(e.data)}
              onCompositionEnd={() => setComposition(null)}
              onChange={(e) => {
                updateQueryParams({
                  search: e.target.value,
                  page: null,
                  member: null,
                });
              }}
            />
          </div>
          {searchPending || membersRequest.loading ? (
            <p role="status">회원 조회 중…</p>
          ) : membersRequest.error ? (
            <p role="alert">
              {membersRequest.error}
              <button onClick={membersRequest.retry}>다시 시도</button>
            </p>
          ) : (
            <MemberActivity
              list={list}
              days={days}
              asOf={params.as_of}
              page={page}
              setPage={setPage}
              pagination={membersRequest.data.pagination}
              pending={status === "pending"}
              onExpand={onExpand}
            />
          )}
        </div>
      </div>
    </>
  );
}
